// @flow
import WebComponent from 'fg-frontend/src/webComponents/WebComponent';

/**
 * Kalkulačka pro výpočet financování vozidel
 * Automaticky se přepočítává při změnách vstupních hodnot v hidden input polích
 * @extends WebComponent
 */
class CalculatorKredit extends WebComponent {
	/**
	 * @param {Object} props - Vlastnosti komponenty
	 */
	constructor(props) {
		super(props);

		/**
		 * Seznam ID elementů pro vstupní hodnoty kalkulačky
		 * @type {string[]}
		 */
		this.inputElements = [
			'carPriceKredit',
			'advancePaymentKredit',
			'interestRateKredit',
			'financingPeriodKredit'
		];

		/**
		 * Pole MutationObserverů a intervalů pro sledování změn
		 * @type {Array<{observer: MutationObserver, intervalId: number}>}
		 */
		this.mutationObservers = [];

		/**
		 * Cache posledních hodnot pro optimalizaci přepočtu
		 * @type {Object.<string, string>}
		 */
		this.lastValues = {};

		this.initEventListeners();
	}

	/**
	 * Lifecycle metoda - spustí se po připojení komponenty do DOM
	 * Inicializuje výpočet a sledování změn
	 */
	didMount() {
		this.calculate();
		this.initMutationObservers();
	}

	/**
	 * Inicializuje event listenery pro input elementy
	 * Nastavuje sledování 'input' a 'change' událostí
	 * @private
	 */
	initEventListeners() {
		this.inputElements.forEach(id => {
			const element = document.getElementById(id);
			if (element) {
				this[id] = element;
				this[id].addEventListener('input', () => this.calculate());
				this[id].addEventListener('change', () => this.calculate());
				this.lastValues[id] = element.value;
			}
		});
	}

	/**
	 * Inicializuje MutationObserver pro sledování změn v hidden input polích
	 * Kombinuje sledování atributů s periodickou kontrolou pro zachycení všech změn
	 * @private
	 */
	initMutationObservers() {
		this.inputElements.forEach(id => {
			const element = document.getElementById(id);
			if (element) {
				const observer = new MutationObserver((mutations) => {
					mutations.forEach((mutation) => {
						if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
							const currentValue = element.value;
							if (this.lastValues[id] !== currentValue) {
								this.lastValues[id] = currentValue;
								this.calculate();
							}
						}
					});
				});

				observer.observe(element, {
					attributes: true,
					attributeFilter: ['value']
				});

				// Periodická kontrola pro případ změny pouze property value
				const intervalId = setInterval(() => {
					const currentValue = element.value;
					if (this.lastValues[id] !== currentValue) {
						this.lastValues[id] = currentValue;
						this.calculate();
					}
				}, 100);

				this.mutationObservers.push({observer, intervalId});
			}
		});
	}

	/**
	 * Hlavní výpočetní funkce kalkulačky
	 * Vypočítává měsíční splátku, balón a celkový úrok na základě vstupních parametrů
	 * @public
	 */
	calculate() {
		try {
			const carPrice = this.getValue('carPriceKredit');
			const advancePayment = this.getValue('advancePaymentKredit');
			const interestRate = this.getValue('interestRateKredit');
			const financingPeriod = this.getValue('financingPeriodKredit');

			if (carPrice <= 0 || financingPeriod <= 0) {
				this.setOutputValues({
					monthlyPaymentKredit: 'N/A',
					totalInterestKredit: 'N/A',
					advancePaymentValueKredit: 'N/A'
				});
				return;
			}

			const advancePaymentValue = carPrice * (advancePayment / 100);
			const amountFinancing = carPrice - advancePaymentValue;


			const monthlyInterest = interestRate / 100 / 12;
			const annuityRepayment = amountFinancing;

			let monthlyPayment;
			if (monthlyInterest === 0) {
				monthlyPayment = annuityRepayment / financingPeriod;
			} else {
				const numerator = monthlyInterest * Math.pow(1 + monthlyInterest, financingPeriod);
				const denominator = Math.pow(1 + monthlyInterest, financingPeriod) - 1;
				monthlyPayment = annuityRepayment * (numerator / denominator);
			}

			const paidInFull = monthlyPayment * financingPeriod;
			const totalInterest = paidInFull - amountFinancing;

			this.setOutputValues({
				monthlyPaymentKredit: monthlyPayment,
				totalInterestKredit: totalInterest,
				advancePaymentValueKredit: advancePaymentValue
			});

		} catch (error) {
			console.error('Chyba při výpočtu:', error);
			this.setOutputValues({
				monthlyPaymentKredit: 'Err',
				totalInterestKredit: 'Err',
				advancePaymentValueKredit: 'Err'
			});
		}
	}

	/**
	 * Nastaví výstupní hodnoty do odpovídajících elementů
	 * @param {Object.<string, number|string>} outputValues - Objekt s ID elementu jako klíčem a hodnotou
	 * @private
	 */
	setOutputValues(outputValues) {
		Object.entries(outputValues).forEach(([id, value]) => {
			this.setValue(id, value);
		});
	}

	/**
	 * Získá a zpracuje hodnotu z hidden input pole
	 * Automaticky převádí čísla s čárkou na tečku, pro datum vrací string
	 * @param {string} id - ID elementu
	 * @returns {number|string} Číselná hodnota pro výpočty nebo string pro datum
	 * @private
	 */
	getValue(id) {
		const element = document.getElementById(id);
		if (!element) {
			console.error(`Element s ID "${id}" nebyl nalezen.`);
			return 0;
		}

		const value = element.value || '';

		// Nahradí čárku tečkou pro správné parsování desetinných čísel
		const normalizedValue = value.replace(',', '.');
		const numValue = parseFloat(normalizedValue || '0');

		return isNaN(numValue) ? 0 : numValue;
	}

	/**
	 * Formátuje číslo jako měnu s mezerami jako oddělovačem tisíců
	 * @param {number} value - Číselná hodnota k zformátování
	 * @returns {string} Zformátované číslo (např. "1 234 567")
	 * @private
	 */
	formatCurrency(value) {
		if (typeof value !== 'number' || isNaN(value)) {
			return '0';
		}

		const rounded = Math.round(value);
		return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	}

	/**
	 * Nastaví hodnotu do output elementu (textContent i value)
	 * @param {string} id - ID cílového elementu
	 * @param {number|string} value - Hodnota k nastavení
	 * @private
	 */
	setValue(id, value) {
		const element = document.getElementById(id);
		if (element) {
			if (typeof value === 'number') {
				const formattedValue = this.formatCurrency(value);
				element.textContent = formattedValue;
				element.value = formattedValue;
			} else {
				element.textContent = value;
				element.value = value;
			}
		}
	}

	/**
	 * Manuální spuštění přepočtu kalkulačky
	 * Užitečné pro externí volání z jiných částí aplikace
	 * @public
	 */
	recalculate() {
		this.calculate();
	}

	/**
	 * Lifecycle metoda - vyčistí zdroje před odstraněním komponenty
	 * Odpojuje MutationObservery a ruší intervaly
	 */
	willUnMount() {
		this.mutationObservers.forEach(({observer, intervalId}) => {
			observer.disconnect();
			clearInterval(intervalId);
		});
		this.mutationObservers = [];
	}
}

export default CalculatorKredit;



// WEBPACK FOOTER //
// ./fe/web-default/src/js/components/CalculatorKredit.js